import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Mic, MicOff, Loader2, Square } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';
import { useHybridSpeechRecognition } from '@/hooks/useHybridSpeechRecognition';
import { TypingIndicator } from '@/components/ui';

interface ChatInputProps {
  onSend: (text: string) => void;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
}

export default function ChatInput({ onSend, inputRef: externalRef }: ChatInputProps) {
  const [value, setValue] = useState('');
  const internalRef = useRef<HTMLTextAreaElement>(null!);
  const textareaRef = externalRef || internalRef;
  const isLoading = useChatStore((s) => s.isLoading);
  const messages = useChatStore((s) => s.messages);

  const {
    isListening,
    transcript,
    isLoading: isTranscribing,
    error: voiceError,
    startListening,
    stopListening,
    clearTranscript,
    mode,
    label,
  } = useHybridSpeechRecognition('es');

  const isVoiceUnavailable = !!voiceError;
  const lastMessage = messages[messages.length - 1];
  const isScopeReplyLocked = (
    lastMessage?.sender === 'ai'
    && lastMessage.clarificationType === 'scope'
    && Boolean(lastMessage.quickReplies?.length)
  );
  const isInputDisabled = isLoading || isScopeReplyLocked;

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [textareaRef]);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  // Sync transcript with value cuando llega
  useEffect(() => {
    if (transcript) {
      setValue(transcript);
      clearTranscript();
    }
  }, [transcript, clearTranscript]);

  useEffect(() => {
    if (!isScopeReplyLocked) return;
    setValue('');
    clearTranscript();
    if (isListening) {
      stopListening();
    }
  }, [clearTranscript, isListening, isScopeReplyLocked, stopListening]);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isInputDisabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleMic = () => {
    if (isScopeReplyLocked || isLoading || isTranscribing) return;
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <div className="p-3 border-t border-border-subtle bg-bg-secondary">
      {/* Error de voz */}
      {voiceError && !isListening && (
        <div className="mb-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded text-[10px] uppercase tracking-wider font-semibold text-red-400/80 flex items-center gap-2">
          <MicOff size={12} />
          <span>{voiceError}</span>
        </div>
      )}

      {/* Transcribiendo */}
      {isTranscribing && (
        <div className="mb-2 px-3 py-1.5 bg-accent-blue/10 border border-accent-blue/20 rounded text-[10px] uppercase tracking-wider font-semibold text-accent-blue/80 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" />
          <span>{mode === 'native' ? 'Procesando voz...' : 'Transcribiendo con OpenAI...'}</span>
        </div>
      )}

      {/* Escuchando - Nativo (auto-stop, no botón) */}
      {isListening && mode === 'native' && (
        <div className="mb-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded text-[10px] uppercase tracking-wider font-semibold text-green-400/80 flex items-center gap-2">
          <Mic size={12} className="animate-pulse" />
          <span>Escuchando... (habla y pausa para enviar)</span>
        </div>
      )}

      {/* Escuchando - OpenAI (con botón de stop) */}
      {isListening && mode === 'openai' && (
        <div className="mb-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded text-[10px] uppercase tracking-wider font-semibold text-blue-400/80 flex items-center gap-2">
          <Mic size={12} className="animate-pulse" />
          <span>Escuchando... (presiona stop para terminar)</span>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 relative flex items-center group">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isScopeReplyLocked
                ? 'Selecciona una de las dos opciones para continuar'
                : isListening
                  ? 'Escuchando... hable ahora'
                  : 'Escribe tu consulta...'
            }
            disabled={isInputDisabled}
            className={`flex-1 glass-card px-3 py-2.5 pr-10 text-sm text-text-primary placeholder-text-subtle outline-none resize-none min-h-[40px] max-h-[120px] transition-all duration-300 ${
                isScopeReplyLocked
                  ? 'border-amber-400/35 bg-amber-400/6 text-text-muted cursor-not-allowed'
                  : isListening 
                    ? 'border-accent-blue/50 shadow-[0_0_20px_rgba(59,130,246,0.15)]' 
                    : 'hover:border-white/10 focus:border-accent-blue/50'
              }`}
            rows={1}
          />
          {/* Botón de micrófono - Nativo: inactivo mientras escucha (auto-stop) */}
          {isListening && mode === 'native' ? (
            <div className="absolute right-2 bottom-1.5 w-7 h-7 flex items-center justify-center rounded-md text-green-400 animate-pulse">
              <Mic size={16} />
            </div>
          ) : (
            <button
              onClick={toggleMic}
              disabled={isInputDisabled || isTranscribing}
              title={isListening ? "Detener" : "Usar micrófono"}
              className={`absolute right-2 bottom-1.5 w-7 h-7 flex items-center justify-center rounded-md transition-all ${
                isListening
                  ? 'text-red-400 bg-red-500/10 animate-pulse hover:bg-red-500/20'
                  : isInputDisabled || isTranscribing
                    ? 'text-text-subtle/50 cursor-not-allowed'
                    : 'text-text-subtle hover:text-text-primary hover:bg-bg-tertiary'
              }`}
            >
              {isListening ? (
                <Square size={14} className="fill-current" />
              ) : isTranscribing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Mic size={16} />
              )}
            </button>
          )}
        </div>
        <button
          onClick={handleSend}
          disabled={isInputDisabled || !value.trim()}
          className="btn-premium w-10 h-10 bg-gradient-to-br from-accent-blue to-accent-purple rounded-lg text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-accent-blue/25"
        >
          <Send size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>

      {isScopeReplyLocked && (
        <div className="mt-2 px-3 py-2 rounded-lg border border-amber-300/70 bg-amber-50 text-[11px] font-semibold text-amber-800">
          Esta respuesta está cerrada a las dos opciones mostradas arriba.
        </div>
      )}
    </div>
  );
}

// Export a way to set value from outside (for suggestion templates)
export function setChatInputValue(ref: React.RefObject<HTMLTextAreaElement | null>, text: string) {
  if (ref.current) {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      'value'
    )?.set;
    nativeInputValueSetter?.call(ref.current, text);
    ref.current.dispatchEvent(new Event('input', { bubbles: true }));

    // Select placeholder text if any
    const placeholderMatch = text.match(/\[([^\]]+)\]/);
    if (placeholderMatch) {
      const start = text.indexOf(placeholderMatch[0]);
      const end = start + placeholderMatch[0].length;
      ref.current.setSelectionRange(start, end);
    }
    ref.current.focus();
  }
}
